import {
	Column,
	DataTypeProvider,
	DataTypeProviderProps,
	IntegratedSorting,
	Sorting,
	SortingState,
} from '@devexpress/dx-react-grid';
import { Grid, Table, TableHeaderRow, VirtualTable } from '@devexpress/dx-react-grid-bootstrap4';
import { IBleTagConnectionHistoryDto, TableId } from 'api/api';
import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Dispatch } from 'redux';
import { getBleConnectionHistory } from 'state/ducks/ble-connection-history/operations';
import { getSettings } from 'state/ducks/history-table-settings/operations';
import { Routes } from 'state/ducks/routes';
import { localized } from 'state/i18n';
import { AppState } from 'state/store';
import { DateFormatterModes, getDateToString } from 'view/shared/components/date-to-string';
import { initEndDate, initStartDate } from '../history/history';
import BiNoData from './bi-no-data/bi-no-data';
import { ROW_HEIGHT } from './bi-table';

interface BleConnectionHistoryForTable extends IBleTagConnectionHistoryDto {
	index: number;
}

const mapStateToProps = (state: AppState, ownProps: AppProps) => {
	return {
		currConnectionHistories: state.bleConnectionHistoryReducer.connectionHistories.find(
			curr => curr.id === ownProps.machineId
		),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
	getBleConnectionHistory: async (serialNumber: string, startDate: Date, endDate: Date) =>
		(await getBleConnectionHistory(serialNumber, startDate, endDate))(dispatch),
	getSettings: async () => (await getSettings())(dispatch),
});

interface AppProps {
	isLoading?: boolean;
	machineId: string;
	autoStartHistoryQuery?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & AppProps;

const getRowId = (row: BleConnectionHistoryForTable) => row.index;

const TableRow = (obj: Table.DataRowProps) => <VirtualTable.Row {...obj} />;
const TableComponent = ({ ...restProps }) => <VirtualTable.Table {...restProps} className="bi-report-table-striped" />;
const HeaderComponent = ({ ...restProps }) => <VirtualTable.TableHead {...restProps} className="table-header" />;

const GenericTypeProvider = (props: DataTypeProviderProps) => <DataTypeProvider {...props} />;

const DateFormatter = (obj: { value: Date }) => {
	return <span>{getDateToString(obj.value, DateFormatterModes.withMinutes)}</span>;
};

const ConnectionStateFormatter = (obj: { value: boolean }) => {
	return <span>{obj.value ? localized('Connected') : localized('Disconnected')}</span>;
};

const LinkFormatter = (obj: { value: string | undefined }) => {
	if (!obj.value) return <span />;
	return <NavLink to={`${Routes.MachineDetails}/${encodeURIComponent(obj.value)}`}>{localized('Link')}</NavLink>;
};

const rootStyle: React.CSSProperties = { height: '100%' };
const Root = (props: Grid.RootProps) => (
	<Grid.Root className="devextreme-react-grid-remove-margin-bottom" {...props} style={rootStyle} />
);

const defaultSorting: Sorting[] = [{ columnName: 'timeOfEvent', direction: 'desc' }];
const sortingColumnExtensions: SortingState.ColumnExtension[] = [{ columnName: 'link', sortingEnabled: false }];

const dateColumns = ['timeOfEvent'];
const connectionColumns = ['isConnected'];
const linkColumns = ['link'];

const BleConnectionHistoryTable: FC<Props> = props => {
	const { isLoading, machineId, autoStartHistoryQuery, currConnectionHistories, getSettings, getBleConnectionHistory } =
		props;

	const columns: Column[] = [
		{ name: 'containerSerialNumber', title: localized('ContainerSerialNumber') },
		{ name: 'timeOfEvent', title: localized('Date') },
		{ name: 'isConnected', title: localized('connectionStatus') },
		{
			name: 'link',
			title: localized('Link'),
			getCellValue: (row: IBleTagConnectionHistoryDto) => row.containerSerialNumber,
		},
	];

	useEffect(() => {
		const init = async () => {
			await getSettings();
			if (autoStartHistoryQuery === true) {
				await getBleConnectionHistory(machineId, initStartDate, initEndDate);
			}
		};

		init();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const noCell = (rest: Table.NoDataCellProps): JSX.Element => (
		<BiNoData isLoading={isLoading} noDataCellProps={rest} tableId={TableId.Histories} />
	);

	let data: BleConnectionHistoryForTable[] = [];
	if (currConnectionHistories?.data) {
		data = currConnectionHistories.data.map((d, index) => ({ ...d, index }));
	}

	return (
		<div className="history-table-container">
			<Grid rows={data} columns={columns} getRowId={getRowId} rootComponent={Root}>
				<GenericTypeProvider for={dateColumns} formatterComponent={DateFormatter} />
				<GenericTypeProvider for={connectionColumns} formatterComponent={ConnectionStateFormatter} />
				<GenericTypeProvider for={linkColumns} formatterComponent={LinkFormatter} />

				<SortingState defaultSorting={defaultSorting} columnExtensions={sortingColumnExtensions} />
				<IntegratedSorting />

				<VirtualTable
					tableComponent={TableComponent}
					headComponent={HeaderComponent}
					height="auto"
					noDataCellComponent={noCell}
					rowComponent={TableRow}
					estimatedRowHeight={ROW_HEIGHT}
				/>
				<TableHeaderRow showSortingControls={true} />
			</Grid>
		</div>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(BleConnectionHistoryTable);
